import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Truck } from "../types";
import api, {
  getUserPermissions,
  updateEntryDirection,
  updateLicensePlate,
  updateEntryLicensePlate,
  deleteEntry,
  deleteTruck,
} from "../utils/api";

// Query keys for consistent caching
export const queryKeys = {
  trucks: ["trucks"] as const,
  devTrucks: (hitOrMiss: string) => ["devTrucks", hitOrMiss] as const,
  userPermissions: ["userPermissions"] as const,
};

// Hook for fetching trucks data
export const useTrucks = (token?: string) => {
  return useQuery({
    queryKey: queryKeys.trucks,
    queryFn: async () => {
      const response = await api.get("/trucks/", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data as Truck[];
    },
    enabled: !!token, // Only run query if token exists
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
    refetchIntervalInBackground: true, // Continue refetching even when tab is not active
  });
};

// Hook for fetching dev trucks data
export const useDevTrucks = (hitOrMiss: string) => {
  return useQuery({
    queryKey: queryKeys.devTrucks(hitOrMiss),
    queryFn: async () => {
      const response = await api.get(`/trucks/hitormiss/${hitOrMiss}`);
      return response.data as Truck[];
    },
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
    refetchIntervalInBackground: true, // Continue refetching even when tab is not active
  });
};

// Hook for fetching user permissions
export const useUserPermissions = () => {
  return useQuery({
    queryKey: queryKeys.userPermissions,
    queryFn: getUserPermissions,
    staleTime: 5 * 60 * 1000, // Permissions don't change often, cache for 5 minutes
  });
};

// Mutation for updating truck direction
export const useUpdateTruckDirection = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      entryId,
      direction,
    }: {
      entryId: string;
      direction: "in" | "out";
    }) => updateEntryDirection(entryId, direction),
    onSuccess: () => {
      // Invalidate and refetch trucks data
      queryClient.invalidateQueries({ queryKey: queryKeys.trucks });
    },
  });
};

// Mutation for updating license plate
export const useUpdateLicensePlate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      truckId,
      newLicensePlate,
    }: {
      truckId: string;
      newLicensePlate: string;
    }) => updateLicensePlate(truckId, newLicensePlate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trucks });
    },
  });
};

// Mutation for updating entry license plate
export const useUpdateEntryLicensePlate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      entryId,
      newLicensePlate,
    }: {
      entryId: string;
      newLicensePlate: string;
    }) => updateEntryLicensePlate(entryId, newLicensePlate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trucks });
    },
  });
};

// Mutation for deleting entry
export const useDeleteEntry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (entryId: string) => deleteEntry(entryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trucks });
    },
  });
};

// Mutation for deleting truck
export const useDeleteTruck = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (truckId: string) => deleteTruck(truckId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trucks });
    },
  });
};

// Mutation for updating truck (general update)
export const useUpdateTruck = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ truck, token }: { truck: Truck; token?: string }) => {
      await api.patch(`/trucks/${truck.id}/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        vehicleNumber: truck.registration,
        note: truck.note,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trucks });
    },
  });
};
